import type { CSSProperties } from 'react';

import { cx } from '@/utils/cx';

type SkeletonVariant = 'text' | 'circular' | 'rectangular';

const VARIANT_CLASSES: Record<SkeletonVariant, string> = {
  text: 'rounded-md',
  circular: 'rounded-full',
  rectangular: 'rounded-lg',
};

const DEFAULT_HEIGHTS: Record<SkeletonVariant, string> = {
  text: '1em',
  circular: '40px',
  rectangular: '120px',
};

type SkeletonAs = 'div' | 'span';

interface SkeletonProps {
  /** Shape variant of the skeleton placeholder. */
  variant?: SkeletonVariant;
  /** Width of the skeleton. Accepts CSS values or numbers (treated as px). */
  width?: string | number;
  /** Height of the skeleton. Accepts CSS values or numbers (treated as px). */
  height?: string | number;
  /** Additional class names for custom overrides. */
  className?: string;
  /**
   * HTML element to render. Use `'span'` when the skeleton is placed inside
   * flow content like a `<p>` or other phrasing-content context — a `<div>`
   * descendant of `<p>` is invalid HTML and triggers a React hydration
   * mismatch (the browser parser closes the `<p>` early). The `'span'`
   * variant gets `inline-block` so width/height still apply. Default `'div'`.
   */
  as?: SkeletonAs;
}

function toCssValue(value: string | number): string {
  return typeof value === 'number' ? `${value}px` : value;
}

export function Skeleton({
  variant = 'text',
  width = '100%',
  height,
  className,
  as = 'div',
}: SkeletonProps) {
  const resolvedHeight = height ?? DEFAULT_HEIGHTS[variant];

  const style: CSSProperties = {
    width: toCssValue(width),
    height: toCssValue(resolvedHeight),
  };

  const Tag = as;

  return (
    <Tag
      className={cx(
        'animate-pulse bg-[var(--color-bg-tertiary)]',
        as === 'span' && 'inline-block',
        VARIANT_CLASSES[variant],
        className
      )}
      style={style}
    />
  );
}

interface SkeletonTextProps {
  /** Number of text lines to render. */
  lines?: number;
  /** Additional class names for the container. */
  className?: string;
}

export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <div className={cx('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          variant='text'
          width={index === lines - 1 ? '75%' : '100%'}
        />
      ))}
    </div>
  );
}
